import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const systemLogQueryKeys = {
  all: ['systemLogs'],
  list: (params) => [...systemLogQueryKeys.all, 'list', params],
};

export const notificationTargetQueryKeys = {
  all: ['notificationTargets'],
  list: (params) => [...notificationTargetQueryKeys.all, 'list', params],
};

export const notificationRuleQueryKeys = {
  all: ['notificationRules'],
  list: (params) => [...notificationRuleQueryKeys.all, 'list', params],
};

// ==================== System Log ====================
/**
 * 시스템 로그 목록 조회
 * @param {Object} params - 쿼리 파라미터
 * @param {string} params.LogLevel - 로그 레벨
 * @param {string} params.ServiceName - 서비스명
 * @param {string} params.ErrorCode - 에러 코드
 * @param {string} params.Keyword - 검색 키워드
 * @param {string} params.FromEventAt - 시작 날짜
 * @param {string} params.ToEventAt - 종료 날짜
 * @param {number} params.page - 페이지 번호
 * @param {number} params.pageSize - 페이지 크기
 * @param {string} params.OrderBy - 정렬 기준
 * @param {string} params.OrderDirection - 정렬 방향 (asc, desc)
 */
export const useSystemLogList = (params) => {
  return useQuery({
    queryKey: systemLogQueryKeys.list(params),
    queryFn: async () => {
      const response = await api.get('/drivecore/syslog/api/SystemLog', { params });
      return response;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * 시스템 로그 추가
 * @param {Object} payload - 로그 데이터
 * @param {string} payload.logLevel - 로그 레벨
 * @param {string} payload.eventAt - 이벤트시작일시
 * @param {string} payload.serviceName - 서비스명
 * @param {string} payload.errorCode - 에러 코드
 * @param {string} payload.message - 에러 메시지
 * @param {string} payload.payload - payload 데이터
 * @param {string} payload.userSysId - 사용자 시스템 ID
 */
export const useCreateSystemLog = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      return await api.post('/drivecore/syslog/api/SystemLog', payload);
    },
    onSuccess: () => {
      // 시스템 로그 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: systemLogQueryKeys.all });
    },
  });
};



// ==================== Notification Target ====================
/**
 * 알림 대상 목록 조회
 * @param {Object} params - 쿼리 파라미터
 * @param {string} params.TargetType - 대상 타입
 * @param {string} params.TargetValue - 대상 값
 * @param {string} params.Page - 페이지 번호
 * @param {string} params.PageSize - 페이지 크기
 */
export const useNotificationTargetList = (params) => {
  return useQuery({
    queryKey: notificationTargetQueryKeys.list(params),
    queryFn: async () => {
      const response = await api.get('/drivecore/syslog/api/NotificationTarget', { params });
      return response;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * 알림 대상 정보 추가
 * @param {Object} payload - 알림 대상 정보
 * @param {string} payload.targetType - 대상 타입
 * @param {string} payload.targetValue - 대상 값
 */

export const useCreateNotificationTarget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      return await api.post('/drivecore/syslog/api/NotificationTarget', payload);
    },
    onSuccess: () => {
      // 알림 대상 정보 캐시 무효화
      queryClient.invalidateQueries({ queryKey: notificationTargetQueryKeys.all });
    },
  });
};

/**
 * 알림 대상 정보 삭제
 * @param {string} targetSysId - 알림 대상 시스템 ID
 */
export const useDeleteNotificationTarget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (targetSysId) => {
      return await api.delete(`/drivecore/syslog/api/NotificationTarget/${targetSysId}`);
    },
    onSuccess: () => {
      // 알림 대상 정보 캐시 무효화
      queryClient.invalidateQueries({ queryKey: notificationTargetQueryKeys.all });
    },
  });
};


// ==================== Notification Rule ====================
/**
 * 알림 규칙 정보 반환
 * @param {object} params - 쿼리 파라미터
 * @param {string} params.RuleName - 규칙 이름
 * @param {string} params.ConditionType - 규칙 조건 타입
 * @param {string} params.IsActive - 규칙 활성 여부
 * @param {string} params.Page - 페이지 번호
 * @param {string} params.PageSize - 페이지 크기
 * @param {string} params.OrderBy - 정렬 기준
 * @param {string} params.OrderDirection - 정렬 방향 (asc, desc)
 */

export const useNotificationRuleList = (params) => {
  return useQuery({
    queryKey: notificationRuleQueryKeys.list(params),
    queryFn: async () => {
      const response = await api.get('/drivecore/syslog/api/NotificationRule', { params });
      return response;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * 알림 규칙 정보 추가
 * @param {Object} payload - 알림 규칙 정보
 * @param {string} payload.ruleName - 규칙 이름
 * @param {string} payload.conditionType - 규칙 조건 타입
 * @param {string} payload.conditionOperator - 규칙 조건 연산자
 * @param {string} payload.conditionValue - 규칙 조건 값
 * @param {string} payload.isActive - 규칙 활성 여부
 * @param {string} payload.userSysId - 사용자 시스템 ID
 * @param {array} payload.notificationTargets - 알림 대상 시스템 ID 목록
 */
export const useCreateNotificationRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      return await api.post('/drivecore/syslog/api/NotificationRule', payload);
    },
    onSuccess: () => {
      // 알림 규칙 정보 캐시 무효화
      queryClient.invalidateQueries({ queryKey: notificationRuleQueryKeys.all });
    },
  });
};

/**
 * 알림 규칙 정보 수정
 * @param {string} ruleSysId - 알림 규칙 시스템 ID
 * @param {Object} payload - 알림 규칙 정보
 * @param {string} payload.ruleName - 규칙 이름
 * @param {string} payload.conditionType - 규칙 조건 타입
 * @param {string} payload.conditionOperator - 규칙 조건 연산자
 * @param {string} payload.conditionValue - 규칙 조건 값
 * @param {string} payload.isActive - 규칙 활성 여부
 * @param {string} payload.userSysId - 사용자 시스템 ID
 * @param {array} payload.notificationTargets - 알림 대상 시스템 ID 목록
 */
export const useUpdateNotificationRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ruleSysId, payload }) => {
      return await api.put(`/drivecore/syslog/api/NotificationRule/${ruleSysId}`, payload);
    },
    onSuccess: () => {
      // 알림 규칙 정보 캐시 무효화
      queryClient.invalidateQueries({ queryKey: notificationRuleQueryKeys.all });
    },
  });
};

/**
 * 알림 규칙 정보 삭제
 * @param {string} ruleSysId - 알림 규칙 시스템 ID
 */
export const useDeleteNotificationRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ruleSysId) => {
      return await api.delete(`/drivecore/syslog/api/NotificationRule/${ruleSysId}`);
    },
    onSuccess: () => {
      // 알림 규칙 정보 캐시 무효화
      queryClient.invalidateQueries({ queryKey: notificationRuleQueryKeys.all });
    },
  });
};